import { debounce, throttle } from './dom-utils.js';
/**
 * Optimized MutationObserver with batching and throttling
 */
export class OptimizedMutationObserver {
    constructor(callback, options = {}) {
        this.pendingMutations = [];
        this.processing = false;
        this.options = {
            debounceMs: 10,
            batchSize: 10,
            ...options
        };
        // Wrap callback with debounce/throttle if specified
        if (options.throttleMs) {
            this.callback = throttle(callback, options.throttleMs);
        }
        else if (options.debounceMs) {
            this.callback = debounce(callback, options.debounceMs);
        }
        else {
            this.callback = callback;
        }
        this.observer = new MutationObserver((mutations) => {
            this.handleMutations(mutations);
        });
    }
    /**
     * Handle mutations with batching
     */
    handleMutations(mutations) {
        this.pendingMutations.push(...mutations);
        if (!this.processing) {
            this.processBatch();
        }
    }
    /**
     * Process mutations in batches
     */
    processBatch() {
        if (this.pendingMutations.length === 0) {
            this.processing = false;
            return;
        }
        this.processing = true;
        const batch = this.pendingMutations.splice(0, this.options.batchSize);
        // Use requestIdleCallback for non-critical mutations
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                this.callback(batch);
                this.processing = false;
                // Process next batch if available
                if (this.pendingMutations.length > 0) {
                    this.processBatch();
                }
            }, { timeout: 50 });
        }
        else {
            // Fallback to setTimeout
            setTimeout(() => {
                this.callback(batch);
                this.processing = false;
                if (this.pendingMutations.length > 0) {
                    this.processBatch();
                }
            }, 0);
        }
    }
    /**
     * Start observing
     */
    observe(target, config) {
        this.observer.observe(target, config);
    }
    /**
     * Stop observing
     */
    disconnect() {
        this.observer.disconnect();
        this.pendingMutations = [];
        this.processing = false;
    }
    /**
     * Get pending mutations count
     */
    getPendingCount() {
        return this.pendingMutations.length;
    }
}
/**
 * Optimized IntersectionObserver with element tracking
 */
export class OptimizedIntersectionObserver {
    constructor(callback, options = {}) {
        this.elements = new WeakMap();
        this.callback = callback;
        this.observer = new IntersectionObserver((entries) => {
            // Filter out duplicate entries for the same element
            const uniqueEntries = this.filterUniqueEntries(entries);
            if (uniqueEntries.length > 0) {
                this.callback(uniqueEntries);
            }
        }, options);
    }
    /**
     * Filter out duplicate entries
     */
    filterUniqueEntries(entries) {
        const seen = new Set();
        const unique = [];
        for (const entry of entries) {
            if (!seen.has(entry.target)) {
                seen.add(entry.target);
                unique.push(entry);
            }
        }
        return unique;
    }
    /**
     * Observe an element
     */
    observe(element) {
        if (!this.elements.has(element)) {
            this.elements.set(element, true);
            this.observer.observe(element);
        }
    }
    /**
     * Stop observing an element
     */
    unobserve(element) {
        this.elements.delete(element);
        this.observer.unobserve(element);
    }
    /**
     * Stop observing all elements
     */
    disconnect() {
        this.observer.disconnect();
        this.elements = new WeakMap();
    }
}
/**
 * Create a one-time intersection observer
 */
export function observeOnce(element, callback, options) {
    const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
            callback(entry);
            observer.disconnect();
        }
    }, options);
    observer.observe(element);
    // Return cleanup function
    return () => observer.disconnect();
}
/**
 * Observe multiple elements with a single observer
 */
export function observeElements(elements, callback, options) {
    const observer = new OptimizedIntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                callback(entry);
            }
        });
    }, options);
    elements.forEach(element => observer.observe(element));
    // Return cleanup function
    return () => observer.disconnect();
}
//# sourceMappingURL=observer-utils.js.map